import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ProjectSummary, SiteData } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { CtaSection } from '@/components/landing/cta-section'
import { ProjectGrid } from '@/components/projects/project-grid'
import { getProjects, getSiteData } from '@/lib/portfolio'
import { loadPageProps } from '@/lib/static-props'
import { REVALIDATE_SECONDS } from '@/constants/site'

type WorkProps = {
    site: SiteData
    projects: Array<ProjectSummary>
}

const Work: FC<WorkProps> = ({ site, projects }): ReactNode => {
    const { profile } = site
    const count = projects.length

    return (
        <Layout site={site}>
            <Seo
                title='Work'
                description={profile?.work_intro ?? undefined}
                ogImage={projects[0]?.cover ?? undefined}
                siteName={profile?.name}
            />

            <section id='work-intro'>
                <Container className='pb-12 pt-16 sm:pb-16 sm:pt-24'>
                    <Eyebrow>
                        {`${count} ${count === 1 ? 'project' : 'projects'}`}
                    </Eyebrow>

                    <h1 className='mt-4 font-heading text-6xl leading-none tracking-tight sm:text-7xl lg:text-8xl'>
                        Work
                    </h1>

                    {profile?.work_intro && (
                        <p
                            data-mdlfy-cms={`profile:${profile.id}:work_intro`}
                            className='mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl'
                        >
                            {profile.work_intro}
                        </p>
                    )}
                </Container>
            </section>

            <section id='projects' aria-label='Projects' className='pb-8'>
                <Container>
                    <ProjectGrid projects={projects} />
                </Container>
            </section>

            <CtaSection profile={profile} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WorkProps> = () => loadPageProps('work', async () => {
    const [site, projects] = await Promise.all([getSiteData(), getProjects()])

    return {
        props: { site, projects },
        revalidate: REVALIDATE_SECONDS
    }
})

export default Work
